if (!customElements.get('br-cart-drawer')) {
  class BrCartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-br-cart-dialog]');
      this.status = this.querySelector('[data-br-cart-drawer-status]');
      this.triggers = [...document.querySelectorAll('[data-br-cart-trigger]')];
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTrigger));
      this.querySelectorAll('[data-br-cart-close]').forEach((button) => button.addEventListener('click', this.onClose));
      this.querySelectorAll('[data-br-cart-change]').forEach((button) => button.addEventListener('click', this.onChange));
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', this.onDialogClosed);
      this.dialog?.addEventListener('cancel', this.onDialogCancel);
      document.addEventListener('click', this.onProductClick);
      this.updateGlobalCount(Number(this.dataset.cartCount || 0));
    }

    disconnectedCallback() {
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this.onTrigger));
      this.querySelectorAll('[data-br-cart-close]').forEach((button) => button.removeEventListener('click', this.onClose));
      this.querySelectorAll('[data-br-cart-change]').forEach((button) => button.removeEventListener('click', this.onChange));
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClosed);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      document.removeEventListener('click', this.onProductClick);
    }

    get rootUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    onTrigger = (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(event.currentTarget);
    };

    onClose = () => this.close();

    onDialogClick = (event) => {
      if (event.target === this.dialog) this.close();
    };

    onDialogCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    onDialogClosed = () => {
      document.documentElement.classList.remove('br-cart-drawer-open');
      document.documentElement.classList.remove('br-cart-drawer-solid-paper');
      this.dialog?.classList.remove('is-closing');
      this.closing = false;
      this.triggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    };

    open(trigger = null) {
      if (!this.dialog) return;
      document.querySelector('.br-header__menu[open]')?.removeAttribute('open');
      this.returnFocus = trigger || document.activeElement;
      const headerRegion = document.querySelector('[data-br-header-region]');
      const useSolidPaper = headerRegion?.classList.contains('br-header-region--paper')
        && !headerRegion.classList.contains('is-scrolled')
        && window.scrollY <= 8;
      document.documentElement.classList.toggle('br-cart-drawer-solid-paper', Boolean(useSolidPaper));
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('br-cart-drawer-open');
      this.triggers?.forEach((cartTrigger) => cartTrigger.setAttribute('aria-expanded', 'true'));
      this.querySelector('[data-br-cart-close]')?.focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog || this.closing) return;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.onDialogClosed();
        }
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      this.closing = true;
      this.dialog.classList.add('is-closing');
      const panel = this.querySelector('[data-br-cart-panel]');
      panel?.addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 240);
    }

    onProductClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const submitter = target?.closest('button[type="submit"], input[type="submit"]');
      const form = submitter?.form;
      if (!(form instanceof HTMLFormElement)) return;
      const action = new URL(form.action, window.location.href);
      if (!action.pathname.endsWith('/cart/add')) return;
      event.preventDefault();
      this.addProduct(form, submitter);
    };

    async addProduct(form, submitter) {
      const originalHtml = submitter?.innerHTML;
      let cartUpdated = false;
      this.setFormStatus(form, 'Adding to cart…');
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
      }

      try {
        const formData = new FormData(form);
        if (!formData.get('id')) throw new Error('Choose an available option first.');
        formData.set('sections', this.dataset.sectionId);
        formData.set('sections_url', window.location.pathname);

        const response = await fetch(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add that item.');

        cartUpdated = true;
        const rendered = await this.resolveRenderedSection(payload);
        if (!rendered) throw new Error('Cart updated, but the cart panel could not refresh.');
        this.setFormStatus(form, 'Added to cart.');
        this.replaceFromSection(rendered, true, submitter);
      } catch (error) {
        if (cartUpdated) {
          this.setFormStatus(form, 'Added to cart. Opening your cart…');
          window.location.assign(`${this.rootUrl}cart`);
          return;
        }
        this.setFormStatus(form, error.message || 'Could not add that item.', true);
      } finally {
        if (submitter?.isConnected) {
          submitter.disabled = false;
          submitter.removeAttribute('aria-busy');
          if (originalHtml !== undefined) submitter.innerHTML = originalHtml;
        }
      }
    }

    onChange = async (event) => {
      const button = event.currentTarget;
      const id = button.dataset.lineKey;
      const quantity = Number(button.dataset.quantity);
      if (!id || Number.isNaN(quantity)) return;
      await this.changeLine(id, Math.max(0, quantity), button);
    };

    async changeLine(id, quantity, button) {
      this.classList.add('is-loading');
      this.setDrawerStatus(quantity === 0 ? 'Removing item…' : 'Updating quantity…');
      button.setAttribute('aria-busy', 'true');
      let cartUpdated = false;

      try {
        const response = await fetch(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id,
            quantity,
            sections: [this.dataset.sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not update the cart.');
        cartUpdated = true;
        const rendered = await this.resolveRenderedSection(payload);
        if (!rendered) throw new Error('Cart updated, but the cart panel could not refresh.');
        this.replaceFromSection(rendered, true);
      } catch (error) {
        if (cartUpdated) {
          window.location.assign(`${this.rootUrl}cart`);
          return;
        }
        this.setDrawerStatus(error.message || 'Could not update the cart.');
        this.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    async resolveRenderedSection(payload) {
      const bundled = payload.sections?.[this.dataset.sectionId];
      if (bundled) return bundled;

      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('sections', this.dataset.sectionId);
      const response = await fetch(sectionUrl.toString(), { headers: { Accept: 'application/json' } });
      const sections = await response.json();
      if (!response.ok) throw new Error('Cart updated, but the cart panel could not refresh.');
      return sections?.[this.dataset.sectionId] || null;
    }

    replaceFromSection(html, shouldOpen = false, returnFocus = null) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector('br-cart-drawer');
      if (!replacement) throw new Error('Cart panel refresh returned invalid markup.');
      const count = Number(replacement.dataset.cartCount || 0);
      this.updateGlobalCount(count);
      this.replaceWith(replacement);
      if (shouldOpen) requestAnimationFrame(() => replacement.open(returnFocus));
    }

    updateGlobalCount(count) {
      document.querySelectorAll('[data-br-cart-count]').forEach((element) => {
        element.textContent = String(count);
      });
    }

    setFormStatus(form, message, isError = false) {
      let status = form.querySelector('[data-br-cart-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'br-cart-form-status';
        status.dataset.brCartStatus = '';
        status.setAttribute('role', isError ? 'alert' : 'status');
        status.setAttribute('aria-live', 'polite');
        form.append(status);
      }
      status.textContent = message;
      status.toggleAttribute('data-error', isError);
      status.setAttribute('role', isError ? 'alert' : 'status');
    }

    setDrawerStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('br-cart-drawer', BrCartDrawer);
}
